import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "./AuthShell";
import { postAuthDestination } from "@/lib/planSelection";

/**
 * Auto-verifies the email token from the URL, then routes the user to
 * their dashboard. If the token is missing, expired, or already used
 * we show a recoverable error state.
 */
export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | ok | error
  const [err, setErr] = useState("");
  const token = new URLSearchParams(location.search).get("token") || "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        if (!cancelled) { setState("error"); setErr("This verification link is missing its token."); }
        return;
      }
      // Guard against React StrictMode's dev-mode double-mount which would
      // otherwise call verify-email twice and see the second call fail as
      // "already used." Session-storage survives the remount.
      const guardKey = `iti.verifyAttempt.${token}`;
      const alreadyTried = sessionStorage.getItem(guardKey);
      if (alreadyTried === "ok") {
        // Prior successful attempt in this browser tab — treat as success.
        await checkAuth();
        if (!cancelled) {
          setState("ok");
          setTimeout(() => { if (!cancelled) navigate(postAuthDestination() || "/dashboard"); }, 800);
        }
        return;
      }
      if (alreadyTried === "pending") {
        // A concurrent StrictMode remount is racing us. Poll auth briefly;
        // if the sibling completes we'll pick up the session cookie.
        for (let i = 0; i < 6 && !cancelled; i++) {
          await new Promise((r) => setTimeout(r, 250));
          try {
            await checkAuth();
            if (sessionStorage.getItem(guardKey) === "ok") {
              if (!cancelled) {
                setState("ok");
                setTimeout(() => { if (!cancelled) navigate(postAuthDestination() || "/dashboard"); }, 800);
              }
              return;
            }
          } catch (_) { /* keep waiting */ }
        }
      }
      sessionStorage.setItem(guardKey, "pending");
      try {
        await api.post("/auth/verify-email", { token });
        await checkAuth();
        sessionStorage.setItem(guardKey, "ok");
        if (cancelled) return;
        setState("ok");
        setTimeout(() => { if (!cancelled) navigate(postAuthDestination() || "/dashboard"); }, 1500);
      } catch (e) {
        // If the token was already consumed by a sibling call in this tab,
        // treat it as success — checkAuth() below will confirm the session.
        sessionStorage.removeItem(guardKey);
        try {
          await checkAuth();
          if (sessionStorage.getItem(guardKey) === "ok") {
            if (!cancelled) {
              setState("ok");
              setTimeout(() => { if (!cancelled) navigate(postAuthDestination() || "/dashboard"); }, 800);
            }
            return;
          }
        } catch (_) { /* fall through to error */ }
        if (cancelled) return;
        setState("error");
        setErr(e?.response?.data?.detail || "This verification link is invalid or has expired.");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state === "verifying") {
    return (
      <AuthShell title="Verifying your email…" subtitle="This only takes a moment." testId="verify-loading-page">
        <div className="flex items-center justify-center py-6" data-testid="verify-spinner">
          <Loader2 className="w-8 h-8 animate-spin text-purple-700" />
        </div>
      </AuthShell>
    );
  }

  if (state === "ok") {
    return (
      <AuthShell title="You're all set!" subtitle="Redirecting you to your dashboard…" testId="verify-ok-page">
        <div className="flex flex-col items-center gap-3 py-4 text-center" data-testid="verify-success">
          <CheckCircle2 className="w-12 h-12 text-emerald-600" />
          <p className="text-sm text-slate-700">Your email is verified and you&apos;re signed in.</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Verification link invalid"
      subtitle="This link may have expired, already been used, or been copied incorrectly."
      testId="verify-error-page"
      footer={
        <>Need a new link? <Link to="/login" className="text-purple-700 font-semibold hover:underline" data-testid="verify-error-signin-link">Sign in</Link> then request one from the notice.</>
      }
    >
      <div className="flex flex-col items-center gap-3 py-4 text-center" data-testid="verify-error">
        <XCircle className="w-12 h-12 text-red-500" />
        <p className="text-sm text-red-700">{err}</p>
      </div>
    </AuthShell>
  );
}
